<template>
  <div>
    <div class="top-bar flex justify-end mb-4">
      <v-button @click="openAddModal" icon="plus" class="ml-4">
        {{ $t('add_supplier') }}
      </v-button>
    </div>

    <b-table
      :data="rows"
      :loading="isLoading"
      paginated
      backend-pagination
      :per-page="perPage"
      :total="total"
      :current-page="page"
      @page-change="onPageChange"
      striped
      bordered
      mobile-cards
      icon-pack="fas"
    >
      <b-table-column field="code" :label="$t('code')" sortable v-slot="props">
        {{ props.row.code }}
      </b-table-column>

      <b-table-column field="name" :label="$t('name')" sortable v-slot="props">
        {{ props.row.name }}
      </b-table-column>

      <b-table-column field="CIF" :label="$t('cif')" sortable v-slot="props">
        {{ props.row.CIF || props.row.cif || '—' }}
      </b-table-column>

      <b-table-column field="actions" :label="$t('actions')" centered width="120" v-slot="props">
        <div class="w-full h-full flex items-center justify-center gap-3">
          <b-tooltip :label="$t('edit')" position="is-top">
            <div @click="openEditModal(props.row)" class="cursor-pointer flex text-blue">
              <b-icon pack="fas" icon="pencil-alt" />
            </div>
          </b-tooltip>
          <b-tooltip
            v-if="Number(props.row.associated_count || 0) === 0"
            :label="$t('delete')"
            type="is-danger"
            position="is-top"
          >
            <div @click="confirmDeleteProvider(props.row)" class="cursor-pointer flex text-red">
              <b-icon pack="fas" icon="trash-alt" />
            </div>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #empty>
        <div class="has-text-centered">{{ $t('no_results') }}</div>
      </template>
    </b-table>

    <b-modal v-model="isModalActive" has-modal-card trap-focus>
      <div class="modal-card" style="width: 640px; max-width: 100%;">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modalTitle }}</p>
        </header>
        <section class="modal-card-body">
          <b-field :label="$t('code')">
            <b-input v-model="form.code" maxlength="100" />
          </b-field>

          <b-field :label="$t('name')">
            <b-input v-model="form.name" maxlength="255" />
          </b-field>

          <b-field :label="$t('cif')">
            <b-input v-model="form.CIF" maxlength="100" />
          </b-field>
        </section>
        <footer class="modal-card-foot justify-end">
          <v-button @click="isModalActive = false">{{ $t('cancel') }}</v-button>
          <v-button @click="submitProvider" :disabled="isSaving" icon="save" color="green">
            {{ $t('save') }}
          </v-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'warehouse-providers',
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  computed: {
    modalTitle() {
      return this.editingId ? this.$t('edit') : this.$t('add_supplier')
    },
  },
  data: () => ({
    rows: [],
    perPage: 15,
    page: 1,
    total: 0,
    isLoading: false,
    isSaving: false,
    isModalActive: false,
    editingId: null,
    form: {
      code: '',
      name: '',
      CIF: '',
    },
  }),
  watch: {
    projectId: {
      immediate: true,
      async handler() {
        this.page = 1
        await this.loadProviders()
      },
    },
  },
  methods: {
    onPageChange(newPage) {
      this.page = newPage
      this.loadProviders()
    },

    openAddModal() {
      this.form = {
        code: '',
        name: '',
        CIF: '',
      }
      this.editingId = null
      this.isModalActive = true
    },

    openEditModal(row) {
      const cif = row.CIF != null && row.CIF !== '' ? row.CIF : (row.cif != null ? row.cif : '')
      this.form = {
        code: row.code || '',
        name: row.name || '',
        CIF: cif,
      }
      this.editingId = row.id
      this.isModalActive = true
    },

    async loadProviders() {
      if (!this.projectId) {
        this.rows = []
        return
      }

      try {
        this.isLoading = true
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/warehouse/providers`, {
          params: {
            page: this.page,
            per_page: this.perPage,
          },
        })
        if (data && data.success) {
          this.rows = data.providers || []
          this.total = typeof data.total === 'number' ? data.total : 0
          if (typeof data.perPage === 'number') {
            this.perPage = data.perPage
          }
          if (typeof data.currentPage === 'number') {
            this.page = data.currentPage
          }
        } else {
          this.rows = []
          this.total = 0
        }
      } catch (error) {
        this.rows = []
        this.$notify.error('error_loading_data')
      } finally {
        this.isLoading = false
      }
    },

    buildPayload() {
      const payload = {
        code: this.form.code,
        name: this.form.name,
      }
      if (this.form.CIF && String(this.form.CIF).trim() !== '') {
        payload.CIF = String(this.form.CIF).trim()
      } else {
        payload.CIF = null
      }
      return payload
    },

    async submitProvider() {
      if (!this.form.code || !this.form.name) {
        this.$notify.error('save_error')
        return
      }

      try {
        this.isSaving = true
        const payload = this.buildPayload()
        if (this.editingId) {
          await axios.put(`/api/v2/project/${this.projectId}/warehouse/providers/${this.editingId}`, payload)
        } else {
          await axios.post(`/api/v2/project/${this.projectId}/warehouse/providers`, payload)
        }
        this.isModalActive = false
        this.$notify.success('save_successful')
        await this.loadProviders()
      } catch (error) {
        const codeErrors = error && error.response && error.response.data && error.response.data.errors
          ? error.response.data.errors.code
          : null
        const isDuplicatedCode = Array.isArray(codeErrors) && codeErrors.length > 0

        this.$notify.error(isDuplicatedCode ? 'existing_unique_provider_code' : 'save_error')
      } finally {
        this.isSaving = false
      }
    },

    confirmDeleteProvider(row) {
      this.$dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('are_you_sure'),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          await this.deleteProvider(row.id)
        },
      })
    },

    async deleteProvider(providerId) {
      try {
        this.isLoading = true
        await axios.delete(`/api/v2/project/${this.projectId}/warehouse/providers/${providerId}`)
        this.$notify.success('removed_successfully')
        if (this.rows.length === 1 && this.page > 1) {
          this.page -= 1
        }
        await this.loadProviders()
      } catch (error) {
        this.$notify.error('save_error')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
